import { Alert, Stepper, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { httpPostGraphql } from "shared/api/httpClient";
import { BackButton } from "shared/components/global/BackButton";
import { Content } from "shared/components/global/Content";
import {
    createIpawsAlertFormState,
    IpawsAlertFormProvider,
    ipawsAlertFormSteppedStateSchema,
    useIpawsAlertForm,
} from "shared/components/ipaws/alert/context";
import { IpawsAlertFormArea } from "shared/components/ipaws/alert/IpawsAlertFormArea";
import { IpawsAlertFormConfiguration } from "shared/components/ipaws/alert/IpawsAlertFormConfiguration";
import { IpawsAlertFormConnectionStatus } from "shared/components/ipaws/alert/IpawsAlertFormConnectionStatus";
import { IpawsAlertFormContent } from "shared/components/ipaws/alert/IpawsAlertFormContent";
import { IpawsAlertFormProfile } from "shared/components/ipaws/alert/IpawsAlertFormProfile";
import { IpawsAlertFormStepLayout } from "shared/components/ipaws/alert/IpawsAlertFormStepLayout";
import { useCustomer } from "shared/graphql/cust";
import { useFormState } from "shared/hooks/useFormState";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { graphql } from "src/gql";

const Page = () => {
    const { alertId } = Route.useParams();
    const { data: customer } = useCustomer();
    const isMobile = useIsMobile();
    const { data } = useSuspenseQuery(query.details(alertId));

    const form = useIpawsAlertForm({
        cascadeUpdates: true,
        initialValues: createIpawsAlertFormState(data.ipawsAlert.capMessage, {
            certId: data.ipawsAlert.certificate.id,
            customerId: customer.id,
            durationMinutes: data.ipawsAlert.durationMinutes,
            mode: ["update", alertId],
        }),
        validate: zodResolver(ipawsAlertFormSteppedStateSchema),
    });

    const [step, setStep] = useFormState(form, "meta.step");

    return (
        <IpawsAlertFormProvider form={form}>
            <Content paper>
                <Content.Heading>
                    <BackButton nav={{ to: "/ipaws/alerts" }} />
                    Update IPAWS Alert
                </Content.Heading>
                <Content.Action>
                    <IpawsAlertFormConnectionStatus />
                </Content.Action>
                <Content.Above>
                    <Alert icon={<IconInfoCircle />} mb="sm" color="blue">
                        <Text size="sm" fw={600}>
                            Update: {data.ipawsAlert.capMessage.identifier}
                        </Text>
                    </Alert>
                </Content.Above>
                <Stepper active={step} onStepClick={setStep} size="xs" radius="sm" styles={{ steps: { display: isMobile ? "none" : "" } }}>
                    <Stepper.Step label="Step 1" description="Profile" allowStepSelect={step >= 0}>
                        <IpawsAlertFormProfile widget />
                    </Stepper.Step>
                    <Stepper.Step label="Step 2" description="Configuration" allowStepSelect={step >= 1}>
                        <IpawsAlertFormConfiguration />
                    </Stepper.Step>
                    <Stepper.Step label="Step 3" description="Geographic Area" allowStepSelect={step >= 2}>
                        <IpawsAlertFormArea />
                    </Stepper.Step>
                    <Stepper.Step label="Step 4" description="Message Content" allowStepSelect={step >= 3}>
                        <IpawsAlertFormStepLayout>
                            <IpawsAlertFormContent />
                        </IpawsAlertFormStepLayout>
                    </Stepper.Step>
                </Stepper>
            </Content>
        </IpawsAlertFormProvider>
    );
};

const query = {
    details: (alertId: string) =>
        queryOptions({
            queryKey: ["ipaws-updates-details", alertId],
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query IpawsUpdateDetails($alertId: UUID!) {
                            ipawsAlert(id: $alertId) {
                                id
                                certificate {
                                    id
                                }
                                capMessage {
                                    ...IpawsCapMessageDetails
                                }
                                durationMinutes
                            }
                        }
                    `),
                    { alertId },
                ),
        }),
};

/** @public */
export const Route = createFileRoute("/_auth/ipaws/update/$alertId")({
    component: Page,
});
